import { ApiResponse, getFromServer, postToServer, BASE_URL } from "server";
import {
  ActivateAnswerMessageOutput,
  ActivateConversationOutput,
  ActivateProblemOutput,
  AddFilesOutput,
  CreateProblemConversationOutput,
  GetAllInSchoolProblemsOutput,
  GetAllProblemsOutput,
  GetMyProblemsOutput,
  RejectProblemOutput,
  RemoveFileFromProblemOutput,
  SubmitProblemOutput,
  UpdateCommentOutput,
  UpdateCorrectorOrSupervisorOutput,
  UpdateObservationOutput,
} from "../OutputsTypes";
import {
  AddFilesInput,
  CreateProblemConversationInput,
  RejectProblemInput,
  SubmitProblemInput,
  UpdateCommentInput,
  UpdateCorrectorOrSupervisorInput,
  UpdateObservationInput,
} from "../InputsTypes";

// Route: GET /problems/all
export const getAllProblems = async (): Promise<
  ApiResponse<GetAllProblemsOutput>
> => {
  const response = await getFromServer<GetAllProblemsOutput>(`/problems/all`);
  return response;
};

// Route: GET /problems/all/in-school

export const getAllInSchoolProblems = async (): Promise<
  ApiResponse<GetAllInSchoolProblemsOutput>
> => {
  const response = await getFromServer<GetAllInSchoolProblemsOutput>(
    `/problems/all/in-school`,
  );
  return response;
};

// Route : GET /problems/mine

export const getMyProblems = async (): Promise<
  ApiResponse<GetMyProblemsOutput>
> => {
  return await getFromServer<GetMyProblemsOutput>(`/problems/mine`);
};

// Route : POST /problems/submit
export const submitProblem = async (
  input: SubmitProblemInput,
): Promise<ApiResponse<SubmitProblemOutput>> => {
  const formData = new FormData();
  formData.append("submissionType", input.submissionType);
  formData.append("referenceType", input.referenceType);

  input.subjectFileIds.forEach((file, index) => {
    formData.append(`subjectFileIds`, file, file.name);
  });

  input.productionFileIds.forEach((file, index) => {
    formData.append(`productionFileIds`, file, file.name);
  });

  if (input.comment) {
    formData.append("comment", input.comment);
  }

  // Toujours envoyer tous les champs de référence, même vides
  if (input.referenceBook) {
    formData.append("referenceBook", JSON.stringify(input.referenceBook));
  } else {
    formData.append("referenceBook", "");
  }

  if (input.referenceOther) {
    formData.append("referenceOther", JSON.stringify(input.referenceOther));
  } else {
    formData.append("referenceOther", "");
  }

  if (input.referenceTest) {
    formData.append("referenceTest", JSON.stringify(input.referenceTest));
  } else {
    formData.append("referenceTest", "");
  }

  return await postToServer<FormData, SubmitProblemOutput>(
    "/problems/submit",
    formData,
  );
};

// Route : POST /problems/activate

export const activateProblem = async (
  id: string,
): Promise<ApiResponse<ActivateProblemOutput>> => {
  return await postToServer<null, ActivateProblemOutput>(
    `/problems/${id}/activate`,
    null,
  );
};

// Route : POST /problems/:id/update-corrector-or-supervisor

export const updateCorrectorOrSupervisor = async (
  id: string,
  input: UpdateCorrectorOrSupervisorInput,
): Promise<ApiResponse<UpdateCorrectorOrSupervisorOutput>> => {
  return await postToServer<
    UpdateCorrectorOrSupervisorInput,
    UpdateCorrectorOrSupervisorOutput
  >(`/problems/${id}/update-corrector-or-supervisor`, input);
};

// Route : POST /problems/:id/update-comment

export const updateComment = async (
  problemId: string,
  input: UpdateCommentInput,
): Promise<ApiResponse<UpdateCommentOutput>> => {
  return await postToServer<UpdateCommentInput, UpdateCommentOutput>(
    `/problems/${problemId}/update-comment`,
    input,
  );
};

// Route : POST /problems/:id/files/add
export const addFiles = async (
  problemId: string,
  input: AddFilesInput,
): Promise<ApiResponse<AddFilesOutput>> => {
  const formData = new FormData();

  input.subjectFileIds.forEach((file) => {
    formData.append("subjectFileIds", file, file.name);
  });

  input.productionFileIds.forEach((file) => {
    formData.append("productionFileIds", file, file.name);
  });

  return await postToServer<FormData, AddFilesOutput>(
    `/problems/${problemId}/files/add`,
    formData,
  );
};

// Route : POST /conversations/problem/create
export const createProblemConversation = async (
  input: CreateProblemConversationInput,
): Promise<ApiResponse<CreateProblemConversationOutput>> => {
  const formData = new FormData();

  formData.append("body", input.body);
  formData.append("exerciseId", input.exerciseId);

  input.files.forEach((file) => {
    formData.append("files", file, file.name);
  });

  return await postToServer<FormData, CreateProblemConversationOutput>(
    "/conversations/problem/create",
    formData,
  );
};

// Route : POST /problems/messages/:messageId/activate-answer
export const activateAnswerMessage = async (
  messageId: string,
): Promise<ApiResponse<ActivateAnswerMessageOutput>> => {
  return await postToServer<{}, ActivateAnswerMessageOutput>(
    `/problems/messages/${messageId}/activate-answer`,
    {},
  );
};

// Route : POST /problems/conversations/:conversationId/activate
export const activateConversation = async (
  conversationId: string,
): Promise<ApiResponse<ActivateConversationOutput>> => {
  return await postToServer<{}, ActivateConversationOutput>(
    `/problems/conversations/${conversationId}/activate`,
    {},
  );
};

// Route : POST /problems/:problemId/reject
export const rejectProblem = async (
  problemId: string,
  observation: string,
): Promise<ApiResponse<RejectProblemOutput>> => {
  const input: RejectProblemInput = {
    observation,
  };
  return await postToServer<RejectProblemInput, RejectProblemOutput>(
    `/problems/${problemId}/reject`,
    input,
  );
};

// Route : POST /problems/:problemId/update-observation
export const updateObservation = async (
  problemId: string,
  observation: string,
): Promise<ApiResponse<UpdateObservationOutput>> => {
  const input: UpdateObservationInput = {
    observation,
  };
  return await postToServer<UpdateObservationInput, UpdateObservationOutput>(
    `/problems/${problemId}/update-observation`,
    input,
  );
};

// Route : POST /problems/:problemId/files/:fileId/remove
export const removeFileFromProblem = async (
  problemId: string,
  fileId: string,
): Promise<ApiResponse<RemoveFileFromProblemOutput>> => {
  return await postToServer<{}, RemoveFileFromProblemOutput>(
    `/problems/${problemId}/files/${fileId}/remove`,
    {},
  );
};

export const addFilesToExercise = async (
  exerciseId: string,
  formData: FormData,
): Promise<ApiResponse<any>> => {
  try {
    const response = await fetch(
      `${BASE_URL}/problems/${exerciseId}/files/add`,
      {
        method: "POST",
        credentials: "include",
        body: formData,
      },
    );

    const data = await response.json();
    return {
      status: response.status,
      body: data,
    };
  } catch (error) {
    return {
      status: 500,
      body: { message: "Erreur réseau lors de l'ajout des fichiers" },
    };
  }
};
